/**
 * Calendar utility for adding events to various calendar apps
 */

interface CalendarEvent {
  title: string;
  description?: string;
  location?: string;
  startDate: Date;
  endDate: Date;
}

/**
 * Format date to ICS format (YYYYMMDDTHHMMSS)
 */
function formatDateToICS(date: Date): string {
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}T${pad(date.getHours())}${pad(date.getMinutes())}00`;
}

/**
 * Format date to ICS UTC format (YYYYMMDDTHHMMSSZ). Using an absolute UTC
 * instant (rather than floating local time) keeps the event correct in any
 * calendar client regardless of the viewer's timezone.
 */
function formatDateToICSUtc(date: Date): string {
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${date.getUTCFullYear()}${pad(date.getUTCMonth() + 1)}${pad(date.getUTCDate())}T${pad(date.getUTCHours())}${pad(date.getUTCMinutes())}${pad(date.getUTCSeconds())}Z`;
}

/**
 * Format date to Google Calendar format (YYYYMMDDTHHMMSS)
 */
function formatDateToGoogle(date: Date): string {
  return formatDateToICS(date);
}

/**
 * Generate ICS file content
 */
export function generateICS(event: CalendarEvent): string {
  const now = new Date();
  const ics = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//Peregovorka//Meeting Room Booking//RU
CALSCALE:GREGORIAN
METHOD:PUBLISH
BEGIN:VEVENT
DTSTART:${formatDateToICSUtc(event.startDate)}
DTEND:${formatDateToICSUtc(event.endDate)}
DTSTAMP:${formatDateToICSUtc(now)}
UID:${Date.now()}@peregovorka.app
SUMMARY:${event.title}
DESCRIPTION:${event.description || ''}
LOCATION:${event.location || ''}
STATUS:CONFIRMED
END:VEVENT
END:VCALENDAR`;
  return ics;
}

/**
 * Download ICS file
 */
export function downloadICS(event: CalendarEvent): void {
  const ics = generateICS(event);
  const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = `${event.title.replace(/\s+/g, '_')}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Generate Google Calendar URL
 */
export function getGoogleCalendarUrl(event: CalendarEvent): string {
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: event.title,
    dates: `${formatDateToGoogle(event.startDate)}/${formatDateToGoogle(event.endDate)}`,
    details: event.description || '',
    location: event.location || '',
  });

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

/**
 * Generate Outlook.com Calendar URL
 */
export function getOutlookCalendarUrl(event: CalendarEvent): string {
  const params = new URLSearchParams({
    path: '/calendar/action/compose',
    rru: 'addevent',
    subject: event.title,
    startdt: event.startDate.toISOString(),
    enddt: event.endDate.toISOString(),
    body: event.description || '',
    location: event.location || '',
  });

  return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`;
}

/**
 * Generate Yahoo Calendar URL
 */
export function getYahooCalendarUrl(event: CalendarEvent): string {
  const duration = Math.round((event.endDate.getTime() - event.startDate.getTime()) / (1000 * 60));
  const hours = Math.floor(duration / 60).toString().padStart(2, '0');
  const minutes = (duration % 60).toString().padStart(2, '0');

  const params = new URLSearchParams({
    v: '60',
    title: event.title,
    st: formatDateToICS(event.startDate),
    dur: `${hours}${minutes}`,
    desc: event.description || '',
    in_loc: event.location || '',
  });

  return `https://calendar.yahoo.com/?${params.toString()}`;
}

/**
 * Open Google Calendar in new tab
 */
export function openGoogleCalendar(event: CalendarEvent): void {
  window.open(getGoogleCalendarUrl(event), '_blank');
}

/**
 * Open Outlook Calendar in new tab
 */
export function openOutlookCalendar(event: CalendarEvent): void {
  window.open(getOutlookCalendarUrl(event), '_blank');
}

/**
 * Open Yahoo Calendar in new tab
 */
export function openYahooCalendar(event: CalendarEvent): void {
  window.open(getYahooCalendarUrl(event), '_blank');
}

/**
 * Create calendar event from reservation data
 */
export function createEventFromReservation(reservation: any): CalendarEvent {
  const today = new Date();
  const startDate = new Date(today);
  const endDate = new Date(today);

  // Parse start time from seconds of day
  const startHours = Math.floor(reservation.seconds_start / 3600);
  const startMinutes = Math.floor((reservation.seconds_start % 3600) / 60);

  startDate.setHours(startHours, startMinutes, 0, 0);
  endDate.setHours(startHours, startMinutes, 0, 0);
  endDate.setSeconds(endDate.getSeconds() + reservation.duration);

  return {
    title: `Переговорка: ${reservation.meeting_room?.title || 'Бронирование'}`,
    description: `Бронирование переговорки через Переговорка 1.1`,
    location: reservation.meeting_room?.title || '',
    startDate,
    endDate,
  };
}
